import React from 'react';
import DOMPurify from 'dompurify';

// The one place email HTML is turned into something renderable.
//
// Incoming mail is the least trusted input in the system, and the auth token
// lives in localStorage, so one crafted message that reaches JS context is a
// full account takeover. Three independent layers stand between the two, and
// each assumes the others have failed:
//
//   1. Whatever the server did. On the inbox path imap_helper.rb#sanitize_html
//      strips the obvious shapes; on the held-message path nothing sanitises at
//      all (api-server.rb hands back msg.html_content through an encoding
//      helper only). Assume nothing from this layer.
//   2. DOMPurify, below, with an allowlist rather than a denylist.
//   3. The sandboxed iframe, which is the layer that actually holds. Without
//      allow-scripts nothing in the document executes at all, and without
//      allow-same-origin it sits in an opaque origin that cannot see
//      window.parent, so even a DOMPurify bypass reaches no token.
//
// Layer 3 is the one to preserve. Adding allow-scripts or allow-same-origin to
// the sandbox attribute -- for auto-height, for anything -- puts us back to
// trusting the sanitiser alone.
//
// It lives in one component because it used to live in two, and they drifted:
// the inbox got the hardened config while the held-message preview kept a
// weaker one that permitted `style` and never forbade iframe/svg/math. Held
// messages come from senders no whitelist has ever matched, so the weaker
// config was guarding the more hostile content. Two copies of a security
// control is one copy and one liability.
const PURIFY_CONFIG = {
    ALLOWED_TAGS: [
        'p', 'br', 'div', 'span', 'b', 'i', 'u', 'strong', 'em',
        'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li',
        'a', 'blockquote', 'pre', 'code',
        'table', 'tr', 'td', 'th', 'thead', 'tbody'
    ],
    ALLOWED_ATTR: ['href', 'title', 'alt'],
    FORBID_TAGS: ['script', 'object', 'embed', 'form', 'input', 'button', 'iframe', 'meta', 'link', 'style', 'svg', 'math'],
    FORBID_ATTR: ['onclick', 'onload', 'onerror', 'onmouseover', 'onfocus', 'srcdoc', 'formaction', 'xlink:href', 'style'],
    ALLOW_DATA_ATTR: false
};

// The iframe is a separate document, so it inherits none of our CSS -- not the
// design tokens, not the font, not the rules that used to style the message
// body. Anything the body needs has to be stated here as a literal. Values
// match --gray-900, --primary-600 and --line-height-relaxed.
const IFRAME_BASE_STYLES =
    'body{font-family:-apple-system,BlinkMacSystemFont,"Segoe UI","Roboto","Oxygen",' +
    '"Ubuntu","Cantarell","Fira Sans","Droid Sans","Helvetica Neue",sans-serif;' +
    'color:#111827;background:transparent;line-height:1.75;margin:0;padding:0;' +
    'word-wrap:break-word;overflow-wrap:break-word;}' +
    'img{display:none !important;}' +
    'a{color:#294081;text-decoration:underline;}' +
    // Newsletters are built from nested layout tables. Their width attributes are
    // stripped by the allowlist, but `max-width` alone cannot shrink a table below
    // its min-content width, which for a dozen nested tables is far wider than the
    // frame -- the body then scrolls sideways and clips text. Fixed layout ignores
    // min-content and wraps to the frame instead.
    'table{table-layout:fixed !important;width:100% !important;max-width:100% !important;}' +
    'td,th{word-break:break-word;overflow-wrap:anywhere;}';

export function buildSandboxedHtml(htmlBody) {
    const clean = DOMPurify.sanitize(htmlBody, PURIFY_CONFIG);
    // <base target="_blank"> is what makes links leave the sandbox, which is why
    // the sandbox attribute has to carry allow-popups. Without it every link is
    // silently dead rather than visibly broken.
    return '<!doctype html><html><head>' +
        '<meta charset="utf-8">' +
        '<base target="_blank">' +
        '<style>' + IFRAME_BASE_STYLES + '</style>' +
        '</head><body>' + clean + '</body></html>';
}

// No auto-height: measuring the content needs allow-same-origin, which would
// trade the layer that holds for a cosmetic gain. Callers size the element
// through className and the document scrolls inside it.
export default function SandboxedHtmlBody({ html, className, title = 'Email body' }) {
    return (
        <iframe
            className={className}
            title={title}
            sandbox="allow-popups allow-popups-to-escape-sandbox"
            referrerPolicy="no-referrer"
            srcDoc={buildSandboxedHtml(html)}
        />
    );
}
